const HIGHLIGHT_ATTR = 'data-e2e-active-target';

function isElement(value: unknown): value is HTMLElement {
  return value instanceof HTMLElement;
}

function markActiveElement(target: HTMLElement): void {
  const previous = document.querySelector(`[${HIGHLIGHT_ATTR}="true"]`);
  if (isElement(previous)) {
    previous.removeAttribute(HIGHLIGHT_ATTR);
  }

  target.setAttribute(HIGHLIGHT_ATTR, 'true');
}

function focusAndCenter(target: HTMLElement): void {
  markActiveElement(target);

  target.scrollIntoView({
    behavior: 'smooth',
    block: 'center',
    inline: 'center',
  });
}

type ReviewPayload = Record<string, unknown>;

function buildFallbackPayload(url: string): ReviewPayload {
  if (url.includes('/ingest/')) {
    return { ok: true };
  }
  if (url.includes('/messages/unread-count')) {
    return { success: true, data: { unreadCount: 0 } };
  }
  if (url.includes('/messages/conversations')) {
    return { success: true, data: [] };
  }
  if (url.includes('/notifications/my')) {
    return { success: true, data: [] };
  }
  if (url.includes('/appointments/my-appointments')) {
    return { success: true, data: [] };
  }
  if (url.includes('/profile-completion')) {
    return {
      success: true,
      data: {
        percentage: 0,
        completedFields: 0,
        totalFields: 0,
      },
    };
  }
  return {};
}

function shouldUseFallback(url: string): boolean {
  return (
    url.includes('/ingest/') ||
    url.includes('/messages/unread-count') ||
    url.includes('/messages/conversations') ||
    url.includes('/notifications/my') ||
    url.includes('/appointments/my-appointments') ||
    url.includes('/profile-completion')
  );
}

function installReviewFetchFallback(): void {
  const originalFetch = window.fetch.bind(window);

  window.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    try {
      return await originalFetch(input, init);
    } catch (error) {
      const method = (init?.method ?? 'GET').toUpperCase();
      if (method !== 'GET') {
        throw error;
      }

      const requestUrl = typeof input === 'string' ? input : input.toString();
      if (!shouldUseFallback(requestUrl)) {
        throw error;
      }
      const payload = buildFallbackPayload(requestUrl);

      return new Response(JSON.stringify(payload), {
        status: 200,
        headers: { 'Content-Type': 'application/json' },
      });
    }
  };
}

export function installE2EVisualReview(): void {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return;
  }

  const styleTag = document.createElement('style');
  styleTag.setAttribute('data-e2e-visual-review-style', 'true');
  styleTag.textContent = `
    html {
      scroll-behavior: smooth;
    }

    [${HIGHLIGHT_ATTR}="true"] {
      outline: 4px solid #ff8a00 !important;
      outline-offset: 3px !important;
      box-shadow: 0 0 0 6px rgba(255, 138, 0, 0.25) !important;
      transition: outline 160ms ease, box-shadow 160ms ease;
    }
  `;
  document.head.appendChild(styleTag);

  document.addEventListener(
    'pointerdown',
    (event: PointerEvent) => {
      if (!isElement(event.target)) {
        return;
      }
      focusAndCenter(event.target);
    },
    true,
  );

  document.addEventListener(
    'focusin',
    (event: FocusEvent) => {
      if (!isElement(event.target)) {
        return;
      }
      focusAndCenter(event.target);
    },
    true,
  );

  installReviewFetchFallback();
}
